import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getAllRevenueCatProducts, deleteRevenueCatProduct } from '../graphql/queries';
import { deleteRevenueCatProduct as deleteProductMutation } from '../graphql/mutation';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

const PRODUCT_TYPES = [
  'WEEKLY',
  'MONTHLY', 
  'YEARLY',
  'INSTANT',
  'ONE_TIME',
  'PROMO'
];

export default function RevenueCatProductsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filter, setFilter] = useState({
    name: '',
    type: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { data, loading, error, refetch } = useQuery(getAllRevenueCatProducts, {
    variables: {
      pagination: {
        skip: page * pageSize,
        take: pageSize
      },
      filter: filter.name || filter.type ? filter : null
    }
  });

  const [deleteProduct] = useMutation(deleteProductMutation);

  useEffect(() => {
    refetch();
  }, [page, pageSize, filter]);

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteProduct({
        variables: { id: productToDelete.id }
      });
      
      setSnackbar({
        open: true,
        message: 'Product deleted successfully',
        severity: 'success'
      });
      
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete product',
        severity: 'error'
      });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTypeColor = (type) => {
    const colors = {
      WEEKLY: 'primary',
      MONTHLY: 'secondary',
      YEARLY: 'success',
      INSTANT: 'info',
      ONE_TIME: 'default',
      PROMO: 'warning'
    };
    return colors[type] || 'default';
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (error) return <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>Error: {error.message}</Box>;

  const products = data?.getAllCreditProducts?.items || [];
  const total = data?.getAllCreditProducts?.total || 0;

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          RevenueCat Products
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/revenuecat-config')}
            sx={{ borderRadius: 2 }}
          >
            Configuration
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/revenuecat-products/create')}
            sx={{ borderRadius: 2 }}
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by name"
                value={filter.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Product Type"
                value={filter.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                InputProps={{
                  startAdornment: <FilterIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              >
                <MenuItem value="">All Types</MenuItem>
                {PRODUCT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Total Products: {total}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Products Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Price</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Limit</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Start Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>End Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} hover>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="medium">
                    {product.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={product.type}
                    color={getTypeColor(product.type)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {product.amount} credits
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    ${product.price}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {product.limit}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(product.startDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(product.endDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/revenuecat-products/${product.id}`)}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/revenuecat-products/${product.id}?edit=true`)}
                        color="secondary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(product)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {total > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center', mx: 2 }}>
            Page {page + 1} of {Math.ceil(total / pageSize)}
          </Typography>
          <Button
            disabled={page >= Math.ceil(total / pageSize) - 1}
            onClick={() => setPage(page + 1)}
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{productToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
